import { Command as CommandPrimitive } from 'cmdk';
import { Check } from 'lucide-react';
import { type KeyboardEvent, useCallback, useRef, useState } from 'react';
import { cn } from '../utils';
import {
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from './command';
import { Skeleton } from './skeleton';

export type Option = Record<'value' | 'label', string> & Record<string, string>;

type AutoCompleteProps = {
  options: Option[];
  value?: Option;
  onValueChange?: (value: Option) => void;
  search: (query: string) => void;
  isLoading?: boolean;
  disabled?: boolean;
  placeholder?: string;
  name: string;
};

export const AutoComplete = ({
  options,
  placeholder,
  name,
  value,
  onValueChange,
  search,
  disabled,
  isLoading = false,
}: AutoCompleteProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const debounce = useRef<NodeJS.Timeout | undefined>(undefined);

  const [isOpen, setOpen] = useState(false);
  const [selected, setSelected] = useState<Option>(value as Option);
  const [inputValue, setInputValue] = useState<string>(value?.label || '');

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      const input = inputRef.current;
      if (!input) {
        return;
      }

      // Keep the options displayed when the user is typing
      if (!isOpen) {
        setOpen(true);
      }

      // This is not a default behaviour of the <input /> field
      if (event.key === 'Enter') {
        const optionToSelect = options.find(
          (option) => option.label === input.value,
        );
        if (optionToSelect && input.value !== '') {
          setSelected(optionToSelect);
          onValueChange?.(optionToSelect);
        } else {
          const option = { label: input.value, value: '' };
          setSelected(option);
          onValueChange?.(option);
        }
      }

      if (event.key === 'Escape') {
        input.blur();
      }

      if (event.key !== 'Escape' && event.key !== 'Enter') {
        if (debounce.current) {
          clearTimeout(debounce.current);
          debounce.current = undefined;
        }
        debounce.current = setTimeout(() => search(input.value), 500);
      }
    },
    [isOpen, options, onValueChange, search],
  );

  const handleBlur = useCallback(() => {
    setOpen(false);
    const input = inputRef.current;
    if (!input) {
      return;
    }
    const optionToSelect = options.find(
      (option) => option.label === input.value,
    );
    if (optionToSelect && input.value !== '') {
      setSelected(optionToSelect);
      onValueChange?.(optionToSelect);
    } else {
      const option = { label: input.value, value: '' };
      setSelected(option);
      onValueChange?.(option);
    }
  }, [onValueChange, options]);

  const handleSelectOption = useCallback(
    (selectedOption: Option) => {
      setInputValue(selectedOption.label);

      setSelected(selectedOption);
      onValueChange?.(selectedOption);

      // This is a hack to prevent the input from being focused after the user selects an option
      // We can call this hack: "The next tick"
      setTimeout(() => {
        inputRef?.current?.blur();
      }, 0);
    },
    [onValueChange],
  );

  return (
    <CommandPrimitive onKeyDown={handleKeyDown}>
      <div>
        <CommandInput
          name={name}
          ref={inputRef}
          value={inputValue}
          onValueChange={isLoading ? undefined : setInputValue}
          onBlur={handleBlur}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          disabled={disabled}
          className="text-base"
        />
      </div>
      <div className="relative mt-1">
        <div
          className={cn(
            'animate-in fade-in-0 zoom-in-95 absolute top-0 z-10 w-full rounded-xl bg-white outline-none',
            isOpen && (options.length > 0 || isLoading) ? 'block' : 'hidden',
          )}
        >
          <CommandList className="rounded-lg ring-1 ring-slate-200">
            {isLoading ? (
              <CommandPrimitive.Loading>
                <div className="p-1">
                  <Skeleton className="h-8 w-full" />
                </div>
              </CommandPrimitive.Loading>
            ) : null}
            {options.length > 0 && !isLoading ? (
              <CommandGroup>
                {options.map((option) => {
                  const isSelected = selected?.value === option.value;
                  return (
                    <CommandItem
                      key={option.value}
                      value={option.label}
                      onMouseDown={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                      }}
                      onSelect={() => handleSelectOption(option)}
                      className={cn(
                        'flex w-full items-center gap-2',
                        !isSelected ? 'pl-8' : null,
                      )}
                    >
                      {isSelected ? <Check className="w-4" /> : null}
                      {option.label}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            ) : null}
          </CommandList>
        </div>
      </div>
    </CommandPrimitive>
  );
};
